import * as React from "react";
import type { ColumnDef } from "@tanstack/react-table";

import { Button, CardHeader, DataTable } from "Components/ui";
import { LockSwitch, ShadowStats } from "api/teleopdLink";
import { formatDateTime, formatRelative } from "utils/format";

const INITIAL_ROWS = 25;

type ShadowCount = ShadowStats["counts"][number];

const columns: ColumnDef<ShadowCount>[] = [
  {
    id: "key",
    header: "Request",
    cell: ({ row }) => (
      <span className="block break-all py-2 font-mono text-xs text-foreground">
        {row.original.key}
      </span>
    ),
  },
  {
    id: "count",
    header: "Count",
    meta: { align: "right" },
    cell: ({ row }) => <span className="tabular-nums text-foreground">{row.original.count}</span>,
  },
];

interface ShadowStatsCardProps {
  stats: ShadowStats | null | undefined;
  lockSwitch: LockSwitch | null | undefined;
}

/** Shadow mode report: staff writes TeleOpd's guard would have blocked if read-only were on. */
const ShadowStatsCard: React.FC<ShadowStatsCardProps> = ({ stats, lockSwitch }) => {
  const [showAll, setShowAll] = React.useState<boolean>(false);
  const counts = stats ? stats.counts.slice().sort((a, b) => b.count - a.count) : [];
  const visible = showAll ? counts : counts.slice(0, INITIAL_ROWS);
  const total = counts.reduce((sum, entry) => sum + entry.count, 0);

  return (
    <section className="space-y-3">
      <CardHeader
        className="p-0"
        title="Would have been blocked"
        description={
          stats
            ? `Staff writes the read-only guard would block, counted since ${formatDateTime(
                stats.since
              )} · reported ${formatRelative(stats.receivedAt)}.`
            : "Staff writes the read-only guard would block, reported by TeleOpd while its lock switch is “shadow”."
        }
      />
      <DataTable
        columns={columns}
        data={visible}
        density="compact"
        enableSorting={false}
        caption="Requests the read-only guard would have blocked"
        getRowId={(entry) => entry.key}
        empty={
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">
            {lockSwitch === "shadow"
              ? "Nothing would have been blocked so far."
              : "No shadow data. TeleOpd collects it while SAP_LOCK=shadow."}
          </p>
        }
        footer={
          counts.length > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
              <span>
                {counts.length} paths · {total} requests. Real staff actions that must stay writable
                go under Rules → Advanced.
              </span>
              {counts.length > INITIAL_ROWS && (
                <Button variant="link" size="xs" onClick={() => setShowAll((value) => !value)}>
                  {showAll ? "Show top 25" : `Show all ${counts.length}`}
                </Button>
              )}
            </div>
          ) : undefined
        }
      />
    </section>
  );
};

export default ShadowStatsCard;
